import { inject, observer } from "mobx-react";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from 'next-intl';
import FooterMenu from "../menus/FooterMenu";
import FacebookIcon from "../svg/FacebookIcon";
import InstagramIcon from "../svg/InstagramIcon";
import TikTokIcon from "../svg/TikTokIcon";
import TwitterXIcon from "../svg/TwitterXIcon";
import WhatsAppIcon from "../svg/WhatsAppIcon";
import YouTubeIcon from "../svg/YouTubeIcon";
import ScrollToTop from "../util/ScrollToTop";
import WhatsAppSupport from "../util/WhatsAppSupport";
import ProductModal from "@/components/products/ProductModal";

const Footer = inject(
  "generalSettingsStore",
  "studioStore",
)(
  observer(({ generalSettingsStore, studioStore }) => {
    const t = useTranslations('General');
    const footerMenus = generalSettingsStore.footerMenuItems || {};
    const siteTitle = generalSettingsStore.siteTitle || "Beaver Next";
    const footerTitle = studioStore?.settings?.footer?.title;
    const bgColor = studioStore?.settings?.footer?.background_color;
    const textColor = studioStore?.settings?.footer?.text_color;
    const logo = studioStore?.settings?.footer?.logo;
    const copyrightText = generalSettingsStore.copyrightText || "Beaver Next";
    const contactsAndSocial = generalSettingsStore.contactsAndSocial || {};

    // Split the footerTitle and extract the last word
    const footerTitleWords = footerTitle?.split(" ") || [];
    const lastWord = footerTitleWords.pop(); // Take the last word
    const remainingTitle = footerTitleWords.join(" "); // Remaining words

    return (
      <>
        <ProductModal />
        <footer style={{ backgroundColor: bgColor, color: textColor }}>
          <div className="container mx-auto py-4 md:py-10 lg:px-3 xl:px-6 2xl:px-8">
            <div className="flex flex-col sm:flex-row justify-center items-center">
              <div className="sm:w-2/6 text-center md:text-start mb-4 md:mb-0">
                {logo?.url ? (
                  <Image
                    src={logo?.url}
                    alt={logo?.alt ? logo?.alt : logo?.title}
                    width={logo?.width}
                    height={logo?.height}
                    className="mb-8 inline-block"
                  />
                ) : (
                  <span className="text-2xl font-bold">{siteTitle}</span>
                )}
                <div className="customer-service font-secondary text-lg mb-4">
                  {t("customer_service")}: {contactsAndSocial.phone}
                </div>
                <div className="social text-center sm:text-start">
                  {contactsAndSocial?.whatsapp && (
                    <Link
                      href={`https://wa.me/${(contactsAndSocial.whatsapp || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Hi! I found your contact on your website.')}`}
                      target="_blank"
                      className="inline-block me-3"
                      title="WhatsApp"
                    >
                      <WhatsAppIcon fill={textColor} size={30} />
                    </Link>
                  )}
                  {contactsAndSocial?.facebook && (
                    <Link
                      href={contactsAndSocial.facebook}
                      target="_blank"
                      className="inline-block me-3"
                      title="Facebook"
                    >
                      <FacebookIcon fill={textColor} size={30} />
                    </Link>
                  )}
                  {contactsAndSocial?.instagram && (
                    <Link
                      href={contactsAndSocial.instagram}
                      target="_blank"
                      className="inline-block me-3"
                      title="Instagram"
                    >
                      <InstagramIcon fill={textColor} size={30} />
                    </Link>
                  )}
                  {contactsAndSocial?.tiktok && (
                    <Link
                      href={contactsAndSocial.tiktok}
                      target="_blank"
                      className="inline-block me-3"
                      title="TikTok"
                    >
                      <TikTokIcon fill={textColor} size={30} />
                    </Link>
                  )}
                  {contactsAndSocial?.x && (
                    <Link
                      href={contactsAndSocial.x}
                      target="_blank"
                      className="inline-block me-3"
                      title="Twitter"
                    >
                      <TwitterXIcon fill={textColor} size={30} />
                    </Link>
                  )}
                  {contactsAndSocial?.youtube && (
                    <Link
                      href={contactsAndSocial.youtube}
                      target="_blank"
                      className="inline-block me-3"
                      title="YouTube"
                    >
                      <YouTubeIcon fill={textColor} size={30} />
                    </Link>
                  )}
                </div>
              </div>
              <div className="sm:w-4/6">
                <h3 className="font-secondary text-2xl md:text-3xl p-2 sm:p-3 md:p-4 tracking-widest text-center md:text-start">
                  {remainingTitle} <span className="font-special">{lastWord}</span>
                </h3>
                <hr className="dotted-spaced dotted-spaced-white m-4 md:hidden" />
                <div className="w-full flex flex-wrap justify-between px-4 md:px-0">
                  {Object.keys(footerMenus).map((key) => (
                    <FooterMenu
                      key={key}
                      name={footerMenus[key].name}
                      items={footerMenus[key].items}
                    />
                  ))}
                </div>
              </div>
            </div>
            <hr className="dotted-spaced dotted-spaced-white m-4 md:hidden" />
            <div className="copyright text-center md:text-end" dangerouslySetInnerHTML={{ __html: copyrightText }} />
          </div>
          <ScrollToTop />
          <WhatsAppSupport />
        </footer>
      </>
    );
  }),
);

export default Footer;
